'use client';

import { type FC, type PropsWithChildren, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { useScrollBlock } from '@/modules/common/hooks/useScrollBlock';
import { isClient } from '@/modules/server/helpers-client';

import {
  ScModal,
  ScModalBg,
  ScModalContainer,
  ScModalContent,
  type ScModalContentProps,
  type ScModalProps,
} from './styled';

export interface GeneralModalProps {
  isPortal?: boolean;
  disableScrollBlock?: boolean;
  hideBackdrop?: boolean;
}

export type ModalProps = PropsWithChildren &
  ScModalContentProps &
  ScModalProps &
  GeneralModalProps;

export const Modal: FC<ModalProps> = ({
  children,
  verticalAlign,
  fullHeight,
  padding,
  isPortal,
  hideBackdrop,
  disableScrollBlock,
  ...props
}) => {
  useScrollBlock(false, disableScrollBlock);

  const modalComponent = useMemo(
    () => (
      <ScModal {...(isPortal && { 'data-portal-modal': 'true' })} {...props}>
        {!hideBackdrop && <ScModalBg {...props} />}
        <ScModalContainer>
          <ScModalContent
            fullHeight={fullHeight}
            padding={padding}
            verticalAlign={verticalAlign}
          >
            {children}
          </ScModalContent>
        </ScModalContainer>
      </ScModal>
    ),
    [
      verticalAlign,
      fullHeight,
      padding,
      children,
      hideBackdrop,
      isPortal,
      props,
    ]
  );

  return isPortal && isClient()
    ? createPortal(modalComponent, document.body)
    : modalComponent;
};
